import { Component, Inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { ListingProfileService } from '@galaxy/listing-profile-common';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ListingProfile, ProjectionFilter } from '@vendasta/listing-products';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AGIDTOKEN } from '@vendasta/local-seo';
import { CategoriesApiService, Category, CategoryRequest, CategoryType, Locale } from '@vendasta/category';

@Component({
  selector: 'local-seo-listing-nap-map-card',
  templateUrl: './listings-nap-map-card.component.html',
  styleUrls: ['./listings-nap-map-card.component.scss'],
  standalone: false,
})
export class ListingsNapMapCardComponent implements OnInit {
  @Input() size = '';
  @Input() overlay: 'half' | 'small' = 'half';
  public mapPixelSize = '';
  public businessProfileCardData$: Observable<BusinessProfileCardData>;
  public isLoaded = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    @Inject(AGIDTOKEN) private accountGroupId$: Observable<string>,
    private listingProfileService: ListingProfileService,
    private readonly categoryService: CategoriesApiService,
  ) {
    this.businessProfileCardData$ = this.accountGroupId$.pipe(
      switchMap((agid) => {
        return this.listingProfileService
          .get(agid, new ProjectionFilter({ napData: true, externalIdentifiers: true }))
          .pipe(
            switchMap((lp: ListingProfile) => {
              const ids = lp.externalIdentifiers?.vCategoryIds || [];
              if (!ids.length) {
                return of({ listingProfile: lp, categoryName: '' });
              }
              const externalId = ids[0];
              return this.getCategoryByExternalIDsAndType(
                [new CategoryRequest({ categoryType: CategoryType.CATEGORY_TYPE_V_CATEGORY, externalId: externalId })],
                Locale[Locale.LOCALE_ENGLISH],
              ).pipe(
                map((categories) => ({
                  listingProfile: lp,
                  categoryName:
                    categories[0]?.translations?.find((t) => t.locale === Locale.LOCALE_ENGLISH)?.name ||
                    categories[0]?.name ||
                    '',
                })),
              );
            }),
            map(({ listingProfile, categoryName }) => ({
              mapOptions: <google.maps.MapOptions>{
                center: {
                  lat: listingProfile.napData.location.latitude,
                  lng: listingProfile.napData.location.longitude - 0.024,
                },
                zoom: 13,
                mapTypeControl: false,
                gestureHandling: 'none',
              },
              markerOptions: <google.maps.MarkerOptions>{
                position: {
                  lat: listingProfile.napData.location.latitude,
                  lng: listingProfile.napData.location.longitude,
                },
                draggable: false,
                title: listingProfile.napData.companyName,
              },
              listingProfile,
              categoryName,
            })),
            shareReplay(1),
          );
      }),
    );
  }

  getCategoryByExternalIDsAndType(categories: CategoryRequest[], locale?: string): Observable<Category[]> {
    return this.categoryService
      .getCategoryByExternalIDsAndType({ categories, locale })
      .pipe(map((res) => res?.category || []));
  }

  validateAddressDisplay(listingProfile: ListingProfile): boolean {
    const napData = listingProfile?.napData;
    const serviceArea = napData?.serviceArea;

    // Show address when the business is not a service area business
    if (!napData?.serviceAreaBusiness) {
      return true;
    }

    // Hide address when the business type indicates address should be hidden
    if (serviceArea?.businessType === 1) {
      return false;
    }

    return true;
  }

  getMatCardClass(): ClassList {
    return {
      'listings-nap-map-card-content': this.size != 'Small',
    };
  }

  getOverlayClass(): ClassList {
    return {
      'business-profile-overlay-small': this.size === 'Small',
      'business-profile-overlay-large': this.size != 'Small',
      'small-width': this.overlay === 'small',
      'half-width': this.overlay != 'small',
    };
  }

  ngOnInit(): void {
    this.isLoaded = true;
    if (this.size === 'Small') {
      this.mapPixelSize = '150px';
    } else {
      this.mapPixelSize = '250px';
    }
  }

  navigateInsideAngular(page: string): void {
    this.router.navigate([`../${page}`], {
      queryParamsHandling: 'preserve',
      relativeTo: this.route,
    });
  }

  truncateString(str: string): string {
    const maxLength = 50;
    if (!str) {
      return '';
    }
    if (str.length > maxLength) {
      return str.slice(0, maxLength) + '...';
    }
    return str;
  }
}

export interface BusinessProfileCardData {
  mapOptions: google.maps.MapOptions;
  markerOptions: google.maps.MarkerOptions;
  listingProfile: ListingProfile;
  categoryName: string;
}

interface ClassList {
  [key: string]: boolean;
}
